// ingredientIcon — map an ingredient name to a small leading emoji glyph.
//
// Mirrors the title-level `recipeEmoji()` pattern in NomLoop.jsx but operates
// at the per-ingredient row level (meal detail sheet + imported recipe detail),
// giving each ingredient line a clean leading icon like the reference recipe
// apps. Emoji are used deliberately: zero bundle cost, no asset pipeline, no
// licensing concerns, and they scale crisply at any size.
//
// Matching is ordered most-specific → least-specific on a lowercased name, with
// word-boundary regexes so "lime" doesn't match "slime" and "egg" doesn't match
// "eggplant" (eggplant is matched first). Aliases that should share one icon are
// grouped in a single pattern (e.g. yoghurt|yogurt|curd, chilli|chili|chile).
//
// Unmapped ingredients fall back to a neutral spoon so every row stays aligned
// and nothing renders blank. Pure function — safe to call inline during render.

const ICON_RULES = [
  // ── Proteins ────────────────────────────────────────────────────────────
  [/\b(prawn|prawns|shrimp)\b/, "🦐"],
  [/\b(crab|lobster)\b/, "🦀"],
  [/\b(salmon|tuna|cod|haddock|hake|trout|mackerel|anchov(y|ies)|sardine|fish|seafood)\b/, "🐟"],
  [/\b(bacon|pancetta|ham|prosciutto)\b/, "🥓"],
  [/\b(chicken|poultry)\b/, "🍗"],
  [/\b(turkey)\b/, "🦃"],
  [/\b(duck)\b/, "🦆"],
  [/\b(beef|steak|mince|lamb|pork|chorizo|sausage|meatball|veal|brisket)\b/, "🥩"],
  [/\b(tofu|tempeh|seitan)\b/, "🧈"],
  [/\b(egg|eggs)\b/, "🥚"],

  // ── Dairy ───────────────────────────────────────────────────────────────
  [/\b(yoghurt|yogurt|curd)\b/, "🥛"],
  [/\b(butter|ghee)\b/, "🧈"],
  [/\b(cheese|parmesan|cheddar|mozzarella|feta|halloumi|paneer|ricotta)\b/, "🧀"],
  [/\b(cream|milk|creme)\b/, "🥛"],

  // ── Vegetables ────────────────────────────────────────────────────────────
  [/\b(tomato|tomatoes|passata|tomato puree)\b/, "🍅"],
  [/\b(onion|onions|shallot|shallots|scallion|spring onion|leek)\b/, "🧅"],
  [/\b(garlic)\b/, "🧄"],
  [/\b(ginger)\b/, "🫚"],
  [/\b(potato|potatoes)\b/, "🥔"],
  [/\b(carrot|carrots)\b/, "🥕"],
  [/\b(chilli|chillies|chili|chilies|chile|cayenne|paprika)\b/, "🌶️"],
  [/\b(pepper|peppers|capsicum|bell pepper)\b/, "🫑"],
  [/\b(mushroom|mushrooms)\b/, "🍄"],
  [/\b(broccoli)\b/, "🥦"],
  [/\b(lettuce|spinach|kale|cabbage|greens|salad|rocket|arugula)\b/, "🥬"],
  [/\b(cucumber)\b/, "🥒"],
  [/\b(corn|sweetcorn|maize)\b/, "🌽"],
  [/\b(aubergine|eggplant)\b/, "🍆"],
  [/\b(avocado)\b/, "🥑"],
  [/\b(pea|peas|edamame)\b/, "🫛"],
  [/\b(bean|beans|chickpea|chickpeas|lentil|lentils)\b/, "🫘"],

  // ── Fruit & aromatics ─────────────────────────────────────────────────────
  [/\b(lemon|lime)\b/, "🍋"],
  [/\b(orange|clementine|mandarin)\b/, "🍊"],
  [/\b(apple)\b/, "🍎"],
  [/\b(banana)\b/, "🍌"],
  [/\b(coconut)\b/, "🥥"],

  // ── Carbs & bakery ──────────────────────────────────────────────────────
  [/\b(rice|risotto|basmati)\b/, "🍚"],
  [/\b(pasta|spaghetti|noodle|noodles|linguine|penne|macaroni|udon|ramen)\b/, "🍝"],
  [/\b(bread|bun|buns|brioche|baguette|tortilla|naan|pita)\b/, "🍞"],
  [/\b(flour|wheat|oats|breadcrumb|breadcrumbs)\b/, "🌾"],

  // ── Pantry & seasoning ──────────────────────────────────────────────────
  [/\b(cashew|cashews|almond|almonds|peanut|peanuts|walnut|walnuts|pecan|nut|nuts)\b/, "🥜"],
  [/\b(salt)\b/, "🧂"],
  [/\b(olive oil|oil|olives)\b/, "🫒"],
  [/\b(honey)\b/, "🍯"],
  [/\b(sugar|syrup)\b/, "🍬"],
  [/\b(chocolate|cocoa|cacao)\b/, "🍫"],
  [/\b(wine|sherry)\b/, "🍷"],
  [/\b(stock|broth|water)\b/, "💧"],
  [/\b(coriander|cilantro|parsley|basil|mint|thyme|rosemary|oregano|dill|sage|herb|herbs)\b/, "🌿"],
  [/\b(cumin|turmeric|cinnamon|cardamom|clove|cloves|nutmeg|masala|curry powder|spice|spices|seasoning)\b/, "🧂"],
];

// Neutral fallback for anything unmapped — keeps every row left-aligned.
const FALLBACK_ICON = "🥄";

/**
 * Return a small emoji icon for an ingredient name. Falls back to a neutral
 * spoon when no rule matches. Always returns a non-empty string.
 * @param {string} name
 * @returns {string}
 */
export function ingredientIcon(name = "") {
  const n = String(name).toLowerCase();
  for (const [re, icon] of ICON_RULES) {
    if (re.test(n)) return icon;
  }
  return FALLBACK_ICON;
}
